/**
 * Analytics event constants and validation schemas
 * Centralized event management to prevent freehand strings and ensure consistency
 */

// Event name constants - use these instead of hardcoded strings
export const EVENTS = {
  // Calculator interactions
  CALCULATOR_OPENED: 'calculator_opened',
  INPUT_CHANGED: 'input_changed', 
  TAX_CONFIG_CHANGED: 'tax_config_changed',
  CALCULATION_COMPLETED: 'calculation_completed',
  
  // Navigation and UI
  TAB_CLICKED: 'tab_clicked',
  OPTIONS_TOGGLED: 'options_toggled',
  CTA_CLICKED: 'cta_clicked',
  
  // Content engagement
  HELP_ACCESSED: 'help_accessed',
  ACCORDION_EXPANDED: 'accordion_expanded',
  LEARN_MORE_CLICKED: 'learn_more_clicked',
  
  // User behavior
  VALIDATION_ERROR: 'validation_error',
  HOME_SEARCH: 'home_search',
  BADGE_CLICKED: 'badge_clicked',
  
  // Legacy events (maintain compatibility)
  LEGACY_SALARY_SUBMITTED: 'UK Take Home Pay Calculator',
  LEGACY_CTA: 'CTA',
  LEGACY_LEARN_MORE: 'Learn More'
};

/**
 * Validate event parameters (simple validation for now)
 * @param {string} eventName - Event name to validate
 * @param {object} params - Event parameters
 * @returns {object} Validation result with isValid and errors
 */
export const validateEventParams = (eventName, params) => {
  const errors = [];
  
  // Basic validation - ensure params is an object
  if (params !== null && typeof params !== 'object') {
    errors.push('Event parameters must be an object');
  }
  
  // Warn about large parameter objects (could indicate PII leakage)
  if (params && Object.keys(params).length > 10) {
    errors.push('Large number of parameters detected - review for PII');
  }
  
  // Check for potentially sensitive data
  if (params) {
    const sensitiveFields = ['email', 'name', 'address', 'phone', 'ip'];
    for (const field of sensitiveFields) {
      if (params[field]) {
        errors.push(`Potentially sensitive field detected: ${field}`);
      }
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Create standard context object for events
 * Tax year and nation should be passed from React config context
 * @param {object} configContext - Config from React app (taxYear, nation, etc.)
 * @param {object} additionalContext - Additional context fields
 * @returns {object} Standard context with additional fields
 */
export const createStandardContext = (configContext = {}, additionalContext = {}) => {
  return {
    page_type: getPageType(),
    route: window.location.pathname,
    tax_year: configContext.taxYear || 'unknown',
    nation: configContext.nation || 'england_wales',
    timestamp: Date.now(),
    ...additionalContext
  };
};

/**
 * Determine page type from current route
 * @returns {string} Page type identifier
 */
const getPageType = () => {
  const pathname = window.location.pathname;
  
  if (pathname === '/') return 'homepage';
  if (pathname.includes('/salary-calculator')) return 'calculator'; 
  if (pathname.includes('/salary-comparison')) return 'comparison';
  if (pathname.includes('/about')) return 'about';
  if (pathname.startsWith('/salary/')) return 'calculator'; // Salary-specific pages
  
  return 'other';
};